import { 
    collection, 
    doc, 
    getDocs, 
    getDoc, 
    setDoc, 
    updateDoc, 
    deleteDoc, 
    query, 
    where, 
    orderBy,
    serverTimestamp,
    Query,
    CollectionReference
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Business } from '../models/Business';

const COLLECTION_NAME = 'businesses';

export const createOrUpdateBusiness = async (businessData: Omit<Business, 'createdAt' | 'updatedAt'>): Promise<void> => {
    try {
        const businessRef = doc(db, COLLECTION_NAME, businessData.id);
        const docSnap = await getDoc(businessRef);

        if (!docSnap.exists()) {
            // Create new business
            await setDoc(businessRef, {
                ...businessData,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });
        } else {
            // Update existing business
            const existingData = docSnap.data();
            await updateDoc(businessRef, {
                ...businessData,
                searchHistoryIds: [...new Set([...(existingData.searchHistoryIds || []), ...(businessData.searchHistoryIds || [])])],
                updatedAt: serverTimestamp()
            });
        }
    } catch (error) {
        console.error('Error creating/updating business:', error);
        throw error;
    }
};

export const getBusinessById = async (id: string): Promise<Business> => {
    try {
        const docRef = doc(db, COLLECTION_NAME, id);
        const docSnap = await getDoc(docRef);
        
        if (!docSnap.exists()) {
            throw new Error('Business not found');
        }

        const data = docSnap.data();
        return {
            id: docSnap.id,
            ...data,
            createdAt: data.createdAt.toDate(),
            updatedAt: data.updatedAt.toDate()
        } as Business;
    } catch (error) {
        console.error('Error fetching business:', error);
        throw error;
    }
};

/**
 * Get all businesses from Firestore
 * @returns Array of businesses with lead status
 */
export const getAllBusinesses = async (): Promise<Business[]> => {
    try {
        const querySnapshot = await getDocs(collection(db, COLLECTION_NAME));
        const businesses = querySnapshot.docs.map(doc => {
            const data = doc.data();
            return {
                id: doc.id,
                ...data,
                createdAt: data.createdAt?.toDate() || new Date(),
                updatedAt: data.updatedAt?.toDate() || new Date(),
                last_analyzed: data.last_analyzed?.toDate() || null,
                last_updated: data.last_updated?.toDate() || new Date(),
                name: data.name || 'Unnamed Business',
                city: data.city || 'Unknown',
                state: data.state || 'Unknown',
                industryType: data.industryType || 'Unknown',
                website: data.website || '',
                phone: data.phone || '',
                address: data.address || '',
                searchHistoryIds: data.searchHistoryIds || [],
                place_id: data.place_id || doc.id,
                has_website: !!data.website,
                is_outdated: data.is_outdated || false,
                website_analyzed: data.website_analyzed || false,
                analysis_result: data.analysis_result,
                hasLead: !!data.leadId
            } as Business;
        });

        console.log('All businesses with lead status:', businesses);
        return businesses;
    } catch (error) {
        console.error('Error getting businesses:', error);
        throw error;
    }
};

export const getBusinessesBySearchHistory = async (searchHistoryId: string): Promise<Business[]> => {
    try {
        const q = query(
            collection(db, COLLECTION_NAME), 
            where('searchHistoryIds', 'array-contains', searchHistoryId)
        );
        const querySnapshot = await getDocs(q);
        
        return querySnapshot.docs.map(doc => {
            const data = doc.data();
            return {
                id: doc.id,
                ...data,
                createdAt: data.createdAt.toDate(),
                updatedAt: data.updatedAt.toDate()
            } as Business;
        });
    } catch (error) {
        console.error('Error fetching businesses by search history:', error);
        throw error;
    }
};

export const deleteBusiness = async (id: string): Promise<void> => {
    try {
        await deleteDoc(doc(db, COLLECTION_NAME, id));
    } catch (error) {
        console.error('Error deleting business:', error);
        throw error;
    }
};

export const createTestBusiness = async (): Promise<void> => {
    try {
        const now = new Date();
        const testBusiness = {
            id: 'test-business-1',
            name: 'Test Business',
            address: '123 Test St',
            city: 'Austin',
            state: 'TX',
            phone: '(512) 555-1234',
            website: 'https://testbusiness.com',
            industryType: 'Technology',
            searchHistoryIds: [],
            place_id: 'test-place-id',
            has_website: true,
            is_outdated: false,
            last_updated: now,
            last_analyzed: now,
            website_analyzed: true,
            analysis_result: {
                detailed_results: ['Test result'],
                score: 85,
                visual_score: 90,
                issues: ['No major issues']
            }
        };

        // Create the document with a specific ID
        const docRef = doc(db, COLLECTION_NAME, testBusiness.id);

        // Add timestamps
        await setDoc(docRef, {
            ...testBusiness,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });

        console.log('Test business created successfully');
    } catch (error) {
        console.error('Error creating test business:', error);
        throw error;
    }
};

export const updateBusinessWithLead = async (businessId: string, leadId: string): Promise<void> => {
    try {
        const businessRef = doc(db, COLLECTION_NAME, businessId);
        await updateDoc(businessRef, {
            leadId,
            hasLead: true,
            updatedAt: serverTimestamp()
        });
        console.log(`Updated business ${businessId} with lead ${leadId}`);
    } catch (error) {
        console.error('Error updating business with lead:', error);
        throw error;
    }
}; 